import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDelete } from '../api/axiosInstance';
import type { OfficeCreate } from '@/generated/api/models/officeCreate';
import type { OfficeUpdate } from '@/generated/api/models/officeUpdate';

export const OFFICES_QUERY_KEY = 'offices';

export interface Office {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  functions: string[];
  headcount: number | null;
  address: string | null;
  address_place_id: string | null;
  address_data: Record<string, unknown> | null;
  city: string | null;
  state_province: string | null;
  country_code: string | null;
  is_active: boolean;
  // Forwarder branch fields (Wave 1.5) — surfaced in the UI only when the
  // tenant persona sells_freight_services is on. All nullable.
  iata_number: string | null;
  customs_broker_filer_code: string | null;
  scac: string | null;
  branch_pnl_currency: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// The generated models ARE the two request bodies — `/api/v1/admin/offices` is a typed,
// schema-first surface, so restating their field lists here is a copy that drifts. Both
// are re-exported under their own names so consumers keep importing them from this hook.
export type { OfficeCreate, OfficeUpdate };

interface ListOfficesParams {
  include_inactive?: boolean;
  limit?: number;
  offset?: number;
}

interface ListOfficesResponse {
  data: Office[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

interface OfficeResponse {
  data: Office;
}

const officeApi = {
  list: async (params?: ListOfficesParams): Promise<ListOfficesResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.include_inactive) queryParams.set('include_inactive', 'true');
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    const queryString = queryParams.toString();
    const url = `/api/v1/admin/offices${queryString ? `?${queryString}` : ''}`;
    return apiGet<ListOfficesResponse>(url);
  },

  get: async (id: string): Promise<OfficeResponse> => {
    return apiGet<OfficeResponse>(`/api/v1/admin/offices/${id}`);
  },

  create: async (data: OfficeCreate): Promise<OfficeResponse> => {
    return apiPost<OfficeResponse>('/api/v1/admin/offices', data);
  },

  update: async (id: string, data: OfficeUpdate): Promise<OfficeResponse> => {
    return apiPut<OfficeResponse>(`/api/v1/admin/offices/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return apiDelete(`/api/v1/admin/offices/${id}`);
  },
};

export function useOffices(params?: ListOfficesParams) {
  return useQuery({
    queryKey: [OFFICES_QUERY_KEY, params],
    queryFn: () => officeApi.list(params),
  });
}

export function useOffice(id: string) {
  return useQuery({
    queryKey: [OFFICES_QUERY_KEY, id],
    queryFn: () => officeApi.get(id),
    enabled: !!id,
  });
}

export function useCreateOffice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OfficeCreate) => officeApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [OFFICES_QUERY_KEY] });
    },
  });
}

export function useUpdateOffice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OfficeUpdate }) =>
      officeApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [OFFICES_QUERY_KEY] });
    },
  });
}

export function useDeleteOffice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => officeApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [OFFICES_QUERY_KEY] });
    },
  });
}
